import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import type { DashboardTotals } from './dashboardSlice';

export type MonthlySalesPoint = {
  name: string;
  totalSales: number;
};

export type CategorySalesPoint = {
  name: string;
  sales: number;
};

export type SalesTransaction = Record<string, unknown>;

export type SalesOverTimePoint = {
  bucket: string;
  label: string;
  revenue: number;
};

export type SalesOverTimeState = {
  series: SalesOverTimePoint[];
  totalRevenue: number;
  bucket: string;
  requestedBucket: string;
  coarsened: boolean;
  timezone: string;
  startDate: string;
  endDate: string;
};

export type ProductSalesGrandTotals = {
  grandTotalSalesAmount: number;
  grandTotalBuyingValue: number;
  grandTotalEstimatedProfit: number;
  grandTotalItemsSold: number;
  grandTotalMassSold: number;
};

// Types for API response structures
type CategorySalesValue = {
  totalSales?: number;
  sellingValue?: number;
  total?: number;
};

type CategorySalesItemShapeA = {
  [categoryName: string]: CategorySalesValue;
};

type CategorySalesItemShapeB = {
  name?: string;
  category?: string;
  categoryName?: string;
  sales?: number;
  totalSales?: number;
  sellingValue?: number;
  total?: number;
};

type CategorySalesRawItem = CategorySalesItemShapeA | CategorySalesItemShapeB;

type MonthKeyedEntry = Record<string, CategorySalesRawItem[]>;

type CategorySalesApiPayload =
  | CategorySalesRawItem[]
  | MonthKeyedEntry[]
  | { data: CategorySalesRawItem[] | MonthKeyedEntry[] };

export interface SalesTransactionsState {
  items: SalesTransaction[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  totalAmount: number;
  currentPage?: number;
  perPage?: number;
  grandTotals: ProductSalesGrandTotals;
  loading: boolean;
  error?: string | null;
}

export interface SalesSliceObject {
  monthlySales: MonthlySalesPoint[];
  categorySales: CategorySalesPoint[];
  salesOverTime: SalesOverTimeState;
  transactions: SalesTransactionsState;
  loadingMonthly: boolean;
  loadingCategory: boolean;
  errorMonthly?: string | null;
  errorCategory?: string | null;
  // Totals displayed in Sales screen widgets
  totals: DashboardTotals | null;
  loadingTotals: boolean;
  errorTotals?: string | null;
}

const emptySalesOverTime: SalesOverTimeState = {
  series: [],
  totalRevenue: 0,
  bucket: '',
  requestedBucket: '',
  coarsened: false,
  timezone: '',
  startDate: '',
  endDate: '',
};

const emptyGrandTotals: ProductSalesGrandTotals = {
  grandTotalSalesAmount: 0,
  grandTotalBuyingValue: 0,
  grandTotalEstimatedProfit: 0,
  grandTotalItemsSold: 0,
  grandTotalMassSold: 0,
};

const initialState: SalesSliceObject = {
  monthlySales: [],
  categorySales: [],
  salesOverTime: emptySalesOverTime,
  transactions: {
    items: [],
    total: 0,
    page: 1,
    pageSize: 100,
    totalPages: 0,
    totalAmount: 0,
    grandTotals: emptyGrandTotals,
    loading: false,
    error: null,
  },
  loadingMonthly: false,
  loadingCategory: false,
  errorMonthly: null,
  errorCategory: null,
  totals: null,
  loadingTotals: false,
  errorTotals: null,
};

const salesSlice = createSlice({
  name: 'backofficeSales',
  initialState,
  reducers: {
    setMonthlySales: (state, action: PayloadAction<MonthlySalesPoint[]>) => {
      state.monthlySales = action.payload;
    },
    setSalesOverTime: (
      state,
      action: PayloadAction<Partial<SalesOverTimeState> | null | undefined>,
    ) => {
      const payload = action.payload || {};
      state.salesOverTime = {
        series: Array.isArray(payload.series)
          ? payload.series.map(p => ({
              bucket: String(p?.bucket ?? ''),
              label: String(p?.label ?? ''),
              revenue: Number(p?.revenue ?? 0),
            }))
          : [],
        totalRevenue: Number(payload.totalRevenue ?? 0),
        bucket: String(payload.bucket ?? ''),
        requestedBucket: String(payload.requestedBucket ?? ''),
        coarsened: Boolean(payload.coarsened),
        timezone: String(payload.timezone ?? ''),
        startDate: String(payload.startDate ?? ''),
        endDate: String(payload.endDate ?? ''),
      };
    },
    setCategorySales: (state, action: PayloadAction<CategorySalesPoint[]>) => {
      state.categorySales = action.payload;
    },
    // Accept raw API payload and normalize to CategorySalesPoint[] for charts
    setCategorySalesFromApi: (
      state,
      action: PayloadAction<CategorySalesApiPayload>,
    ) => {
      const payload = action.payload;
      let raw: CategorySalesRawItem[] | MonthKeyedEntry[] = [];

      // Extract data from payload
      if (payload && typeof payload === 'object') {
        if ('data' in payload && Array.isArray(payload.data)) {
          raw = payload.data;
        } else if (Array.isArray(payload)) {
          raw = payload;
        }
      }

      const currentMonthIndex = new Date().getMonth(); // 0..11

      // Detect if items are keyed by month number mapping to an array of categories
      let list: CategorySalesRawItem[] = [];
      if (
        Array.isArray(raw) &&
        raw.length > 0 &&
        typeof raw[0] === 'object' &&
        !Array.isArray(raw[0])
      ) {
        const monthKey = String(currentMonthIndex + 1);
        const monthEntry = raw[currentMonthIndex];
        // Type guard to check if it's a MonthKeyedEntry
        const isMonthKeyedEntry = (
          entry: CategorySalesRawItem | MonthKeyedEntry,
        ): entry is MonthKeyedEntry => {
          if (
            entry === null ||
            typeof entry !== 'object' ||
            Array.isArray(entry)
          ) {
            return false;
          }
          const entryRecord = entry as Record<string, unknown>;
          return (
            monthKey in entryRecord && Array.isArray(entryRecord[monthKey])
          );
        };
        if (monthEntry && isMonthKeyedEntry(monthEntry)) {
          list = monthEntry[monthKey] as CategorySalesRawItem[];
        } else {
          list = raw as CategorySalesRawItem[];
        }
      } else {
        list = raw as CategorySalesRawItem[];
      }

      const normalized: CategorySalesPoint[] = (list || []).map(
        (item: CategorySalesRawItem) => {
          // Shape A: { plumbing: { totalSales: 0 } }
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            const keys = Object.keys(item);
            if (keys.length === 1) {
              const categoryName = keys[0];
              const values = (item as CategorySalesItemShapeA)[categoryName];
              if (values && typeof values === 'object') {
                const sales = Number(
                  values?.totalSales ??
                    values?.sellingValue ??
                    values?.total ??
                    0,
                );
                return { name: String(categoryName || ''), sales };
              }
            }
            // Shape B: { name, sales } or { category, totalSales }
            const shapeB = item as CategorySalesItemShapeB;
            const name: string = String(
              shapeB?.name ?? shapeB?.category ?? shapeB?.categoryName ?? '',
            );
            const sales = Number(
              shapeB?.sales ??
                shapeB?.totalSales ??
                shapeB?.sellingValue ??
                shapeB?.total ??
                0,
            );
            return { name, sales };
          }
          return { name: '', sales: 0 };
        },
      );

      state.categorySales = normalized.filter(c => c.name.length > 0);
    },
    setTransactions: (
      state,
      action: PayloadAction<{
        items: SalesTransaction[];
        total?: number;
        page?: number;
        pageSize?: number;
        totalPages?: number;
        totalAmount?: number;
        currentPage?: number;
        perPage?: number;
        grandTotals?: Partial<ProductSalesGrandTotals>;
      }>,
    ) => {
      state.transactions.items = action.payload.items;
      if (typeof action.payload.total === 'number')
        state.transactions.total = action.payload.total;
      if (typeof action.payload.page === 'number')
        state.transactions.page = action.payload.page;
      if (typeof action.payload.pageSize === 'number')
        state.transactions.pageSize = action.payload.pageSize;
      if (typeof action.payload.totalPages === 'number')
        state.transactions.totalPages = action.payload.totalPages;
      if (typeof action.payload.totalAmount === 'number')
        state.transactions.totalAmount = action.payload.totalAmount;
      if (typeof action.payload.currentPage === 'number')
        state.transactions.currentPage = action.payload.currentPage;
      if (typeof action.payload.perPage === 'number')
        state.transactions.perPage = action.payload.perPage;
      if (action.payload.grandTotals) {
        state.transactions.grandTotals = {
          grandTotalSalesAmount: Number(
            action.payload.grandTotals.grandTotalSalesAmount ?? 0,
          ),
          grandTotalBuyingValue: Number(
            action.payload.grandTotals.grandTotalBuyingValue ?? 0,
          ),
          grandTotalEstimatedProfit: Number(
            action.payload.grandTotals.grandTotalEstimatedProfit ?? 0,
          ),
          grandTotalItemsSold: Number(
            action.payload.grandTotals.grandTotalItemsSold ?? 0,
          ),
          grandTotalMassSold: Number(
            action.payload.grandTotals.grandTotalMassSold ?? 0,
          ),
        };
      }
    },
    setTransactionsLoading: (state, action: PayloadAction<boolean>) => {
      state.transactions.loading = action.payload;
    },
    setTransactionsError: (state, action: PayloadAction<string | null>) => {
      state.transactions.error = action.payload;
    },
    setTransactionsPagination: (
      state,
      action: PayloadAction<{ page?: number; pageSize?: number }>,
    ) => {
      if (typeof action.payload.page === 'number')
        state.transactions.page = action.payload.page;
      if (typeof action.payload.pageSize === 'number')
        state.transactions.pageSize = action.payload.pageSize;
    },
    setLoadingMonthly: (state, action: PayloadAction<boolean>) => {
      state.loadingMonthly = action.payload;
    },
    setLoadingCategory: (state, action: PayloadAction<boolean>) => {
      state.loadingCategory = action.payload;
    },
    setErrorMonthly: (state, action: PayloadAction<string | null>) => {
      state.errorMonthly = action.payload;
    },
    setErrorCategory: (state, action: PayloadAction<string | null>) => {
      state.errorCategory = action.payload;
    },
    setSalesTotals: (state, action: PayloadAction<DashboardTotals | null>) => {
      state.totals = action.payload;
    },
    setSalesTotalsLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingTotals = action.payload;
    },
    setSalesTotalsError: (state, action: PayloadAction<string | null>) => {
      state.errorTotals = action.payload;
    },
  },
});

export const {
  setMonthlySales,
  setSalesOverTime,
  setCategorySales,
  setCategorySalesFromApi,
  setTransactions,
  setTransactionsLoading,
  setTransactionsError,
  setTransactionsPagination,
  setLoadingMonthly,
  setLoadingCategory,
  setErrorMonthly,
  setErrorCategory,
  setSalesTotals,
  setSalesTotalsLoading,
  setSalesTotalsError,
} = salesSlice.actions;

export default salesSlice.reducer;
